import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'

export default function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim() || !password) return
    setLoading(true)
    setError('')

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })

    if (signInError) {
      // Friendly error mapping
      const msg = signInError.message || ''
      if (/invalid login credentials/i.test(msg)) {
        setError(t('auth.error_invalid_credentials'))
      } else if (/email not confirmed/i.test(msg)) {
        setError(t('auth.error_email_not_confirmed'))
      } else {
        setError(msg || t('auth.error_generic'))
      }
      setLoading(false)
      return
    }

    navigate('/')
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brand}>
          <span style={styles.brandMerq}>Merq</span>
          <span style={styles.brandLog}>Log</span>
        </div>
        <p style={styles.tagline}>{t('app.tagline')}</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            <span style={styles.labelText}>{t('auth.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              required
              style={styles.input}
            />
          </label>

          <label style={styles.label}>
            <span style={styles.labelText}>{t('auth.password')}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              style={styles.input}
            />
          </label>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" disabled={loading} style={{
            ...styles.submit,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}>
            {loading ? t('auth.signing_in') : t('auth.login')}
          </button>

          <Link to="/forgot-password" style={styles.forgot}>
            {t('auth.forgot_password')}
          </Link>
        </form>

        <div style={styles.divider} />

        <div style={styles.switchRow}>
          <span style={styles.switchText}>{t('auth.no_account')}</span>
          <Link to="/signup" style={styles.switchLink}>
            {t('auth.signup')}
          </Link>
        </div>
      </div>
    </div>
  )
}

const plum = '#1E1040'
const teal = '#0D9488'

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    background: 'linear-gradient(135deg, #F7F9FC 0%, #E6FFFA 100%)',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: '#fff',
    borderRadius: '20px',
    padding: '40px 32px',
    boxShadow: '0 10px 40px rgba(30, 16, 64, 0.08)',
    border: '1px solid rgba(30, 16, 64, 0.06)',
  },
  brand: {
    fontFamily: "'Fraunces', serif",
    fontSize: '36px',
    fontWeight: 900,
    letterSpacing: '-1px',
    textAlign: 'center',
    marginBottom: '6px',
  },
  brandMerq: { color: plum },
  brandLog: { color: teal, fontStyle: 'italic' },
  tagline: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: 500,
    margin: '0 0 32px 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  labelText: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  input: {
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1.5px solid #E5E7EB',
    fontSize: '15px',
    color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#B91C1C',
    fontSize: '13px',
    padding: '10px 12px',
    borderRadius: '8px',
    fontWeight: 500,
  },
  submit: {
    marginTop: '6px',
    padding: '14px',
    background: teal,
    color: '#042f2e',
    border: 'none',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  forgot: {
    marginTop: '4px',
    textAlign: 'center',
    fontSize: '13px',
    color: teal,
    fontWeight: 600,
    textDecoration: 'none',
  },
  divider: {
    height: '1px',
    background: '#E5E7EB',
    margin: '24px 0 20px 0',
  },
  switchRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    fontSize: '14px',
  },
  switchText: { color: '#6B7280' },
  switchLink: {
    color: teal,
    fontWeight: 700,
    textDecoration: 'none',
  },
}
